import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import RequirementsProgress, { reqStats } from "../components/RequirementsProgress";
import OnTheClockWidget from "../components/OnTheClockWidget";
import { toast } from "sonner";
import { Layers, AlertCircle, CheckCircle2, Loader2, Trash2, Clock, Search, Inbox, Bell, X, XCircle, History, AlertTriangle, ChevronDown, RefreshCw, ArrowUp, ArrowDown, ArrowUpDown, Send, Download, UserCheck, UserCircle2, MapPin } from "lucide-react";
import { slaInfo, historyWithDurations, humanizeDuration, abstractorSla } from "../lib/sla";
import { STATUS_STAGES } from "../lib/workflows";
import { stateName } from "../lib/states";

const TERMINAL = ["Completed", "Cancelled", "Submitted", "Rejected"];
const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"];

function agingInfo(o) {
  if (TERMINAL.includes(o.status)) return null;
  const ts = [o.created_at, o.updated_at, ...((o.status_history || []).map((e) => e.timestamp))]
    .filter(Boolean).map((t) => new Date(t).getTime()).filter((n) => !Number.isNaN(n));
  if (!ts.length) return null;
  const hrs = (Date.now() - Math.max(...ts)) / 3.6e6;
  const label = hrs >= 24 ? `${Math.floor(hrs / 24)}d idle` : `${Math.floor(hrs)}h idle`;
  if (hrs >= 48) return { level: "red", label };
  if (hrs >= 24) return { level: "amber", label };
  return null;
}

const bucketOf = (o) => (["Completed", "Cancelled", "Submitted", "Rejected"].includes(o.status) ? "closed" : o.assigned_to ? "accepted" : "unaccepted");
const cityOf = (addr) => {
  const m = (addr || "").split(",");
  return m.length >= 2 ? m[m.length - 2].trim() : "";
};

const StatusPill = ({ status }) => {
  const map = {
    "Open Order": "bg-amber-100 text-amber-800 border-amber-300",
    "In Progress": "bg-blue-100 text-blue-800 border-blue-300",
    "Completed": "bg-emerald-100 text-emerald-800 border-emerald-300",
    "Submitted": "bg-teal-100 text-teal-800 border-teal-300",
    "Cancelled": "bg-rose-100 text-rose-800 border-rose-300",
    "Pending for Documents": "bg-cyan-100 text-cyan-800 border-cyan-300",
    "Tax Pending": "bg-orange-100 text-orange-800 border-orange-300",
    "Need to Call for Taxes": "bg-red-100 text-red-800 border-red-300",
    "Typing Pending": "bg-indigo-100 text-indigo-800 border-indigo-300",
    "ABS Order Quality/Final Review": "bg-purple-100 text-purple-800 border-purple-300",
  };
  return <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${map[status] || map["Open Order"]}`}>{status}</span>;
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("unaccepted");
  const [kpiCat, setKpiCat] = useState(null);
  const [slaOnly, setSlaOnly] = useState(false);
  const [blockedOnly, setBlockedOnly] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [q, setQ] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [productFilter, setProductFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [closedStatus, setClosedStatus] = useState("");
  const [closedFrom, setClosedFrom] = useState("");
  const [closedTo, setClosedTo] = useState("");
  const [sortBy, setSortBy] = useState("");
  const [sortDir, setSortDir] = useState("asc");
  const [exportFrom, setExportFrom] = useState("");
  const [exportTo, setExportTo] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [reOcr, setReOcr] = useState(false);
  const [reprocessInfo, setReprocessInfo] = useState(null);

  const [newAlert, setNewAlert] = useState({ count: 0, orders: [], latest: null });
  const seenRef = useRef(localStorage.getItem("orderAlertSeen") || new Date().toISOString());

  const load = async () => {
    setLoading(true);
    try {
      const [o, s, e] = await Promise.all([api.get("/orders"), api.get("/orders/stats"), api.get("/users")]);
      setOrders(o.data); setStats(s.data); setEmployees(e.data);
    } catch { toast.error("Failed to load master dashboard"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { api.get("/mailbox/reprocess-status").then((r) => setReprocessInfo(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    let active = true;
    const poll = async () => {
      try {
        const { data } = await api.get("/orders/new-alert", { params: { since: seenRef.current } });
        if (active && data.count > 0) setNewAlert({ count: data.count, orders: data.orders || [], latest: data.latest });
      } catch { /* silent */ }
    };
    poll();
    const id = setInterval(poll, 30000);
    return () => { active = false; clearInterval(id); };
  }, []);

  const dismissAlert = () => {
    if (newAlert.latest) { seenRef.current = newAlert.latest; localStorage.setItem("orderAlertSeen", newAlert.latest); }
    setNewAlert({ count: 0, orders: [], latest: null });
  };
  const viewNewOrders = () => { setTab("unaccepted"); load(); dismissAlert(); };

  const assign = async (orderId, empId) => {
    try {
      await api.patch(`/orders/${orderId}/assign`, { assigned_to: empId || null });
      toast.success(empId ? "Order assigned" : "Order unassigned");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Assignment failed"); }
  };

  const setOrderState = async (orderId, state) => {
    try { await api.patch(`/orders/${orderId}/state`, { state }); toast.success(`State set to ${state}`); load(); }
    catch { toast.error("Could not set state"); }
  };

  const assignAllSuggested = async () => {
    const targets = orders.filter((o) => bucketOf(o) === "unaccepted").map((o) => ({ o, sug: suggestFor(o) })).filter((x) => x.sug);
    if (!targets.length) { toast.info("No unassigned orders have an eligible suggestion"); return; }
    if (!window.confirm(`Assign ${targets.length} unassigned order(s) to their suggested searchers?`)) return;
    let ok = 0;
    for (const { o, sug } of targets) {
      try { await api.patch(`/orders/${o.id}/assign`, { assigned_to: sug.id }); ok++; } catch {}
    }
    toast.success(`Assigned ${ok} of ${targets.length} order(s)`);
    load();
  };

  const deleteAllOrders = async () => {
    if (!window.confirm("Delete ALL orders? This permanently removes every order and cannot be undone.")) return;
    try { const { data } = await api.delete("/orders"); toast.success(`${data.deleted} order(s) deleted`); load(); }
    catch { toast.error("Could not delete orders"); }
  };

  const mergeDuplicates = async () => {
    try {
      const { data: pre } = await api.get("/orders/duplicates");
      if (!pre.duplicate_groups) { toast.info("No duplicate order numbers found"); return; }
      if (!window.confirm(`Found ${pre.duplicate_groups} duplicated order number(s) (${pre.extra_rows} extra row(s)). Merge each into its OLDEST row and move all attachments onto it? This cannot be undone.`)) return;
      const { data } = await api.post("/orders/dedupe");
      toast.success(`Merged ${data.merged_groups} group(s) · removed ${data.orders_deleted} duplicate row(s) · moved ${data.attachments_moved} attachment(s)`);
      load();
    } catch { toast.error("Could not merge duplicates"); }
  };

  const syncNow = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/mailbox/sync");
      toast.success(data.message || "Sync started — new orders will appear shortly.");
      setTimeout(load, 5000);
    } catch (e) { toast.error(e?.response?.data?.detail || "Sync failed — check mailbox settings"); }
    finally { setSyncing(false); }
  };

  const pollReocr = async (attempt = 0) => {
    try {
      const { data } = await api.get("/mailbox/reprocess-status");
      if (data.status === "running" && attempt < 120) { setTimeout(() => pollReocr(attempt + 1), 4000); return; }
      if (data.status === "done") {
        setReprocessInfo(data);
        toast.success(`Re-OCR complete — recovered ${data.updated || 0} order(s)${data.still_blank ? `, ${data.still_blank} still missing details` : ""}.`, { duration: 8000 });
        load();
      } else if (data.status === "error") { toast.error("Re-OCR hit an error — check the logs."); }
    } catch { /* stop */ }
    setReOcr(false);
  };

  const reocrAllBlank = async () => {
    if (!window.confirm("Re-OCR & AI re-extract Borrower/Property for ALL orders currently missing details?\n\nThis reads the original email/attachment (with OCR for scanned order sheets) for every blank order. It may take a few minutes.")) return;
    setReOcr(true);
    try {
      const { data } = await api.post("/mailbox/resync?days=1&clear=false&force_reprocess=true&reprocess_days=3650");
      toast.success(data.message || "Re-OCR started — details will fill in shortly.");
      setTimeout(() => pollReocr(0), 4000);
    } catch (e) { toast.error(e?.response?.data?.detail || "Re-OCR failed"); setReOcr(false); }
  };

  const updateStatus = async (o, newStatus) => {
    if (newStatus === o.status) return;
    try {
      const { data } = await api.patch(`/orders/${o.id}/status`, { status: newStatus });
      setOrders((prev) => prev.map((x) => (x.id === o.id ? { ...x, ...data } : x)));
      toast.success(`Status → ${newStatus}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update status"); }
  };

  const loadByEmp = {};
  orders.forEach((o) => { if (o.assigned_to && !TERMINAL.includes(o.status)) loadByEmp[o.assigned_to] = (loadByEmp[o.assigned_to] || 0) + 1; });
  // Every user (admins + employees) is selectable on every order — no state restriction.
  const suggestFor = () => {
    if (!employees.length) return null;
    return employees.slice().sort((a, b) => (loadByEmp[a.id] || 0) - (loadByEmp[b.id] || 0))[0];
  };

  const products = Array.from(new Set(orders.map((o) => o.product_type).filter(Boolean))).sort();
  const countFor = (f) => orders.filter((o) => bucketOf(o) === f).length;

  const catMatch = (o, cat) => {
    if (cat === "total") return true;
    if (cat === "unassigned") return !o.assigned_to && !TERMINAL.includes(o.status);
    if (cat === "inprogress") return o.status.startsWith("InProgress");
    if (cat === "completed") return o.status === "Completed";
    if (cat === "submitted") return o.status === "Submitted";
    if (cat === "cancelled") return o.status === "Cancelled";
    if (cat === "needs_details") return (!o.borrower || !o.property_address) && !TERMINAL.includes(o.status);
    if (cat === "abstractor") return ["Abstractor Order Received", "Abstractor Pending", "Clarification Needed"].includes(o.status) || !!o.assignment_package;
    return true;
  };
  const kpiCounts = {
    total: orders.length,
    unassigned: orders.filter((o) => catMatch(o, "unassigned")).length,
    inprogress: orders.filter((o) => catMatch(o, "inprogress")).length,
    completed: orders.filter((o) => catMatch(o, "completed")).length,
    submitted: orders.filter((o) => catMatch(o, "submitted")).length,
    cancelled: orders.filter((o) => catMatch(o, "cancelled")).length,
    needs_details: orders.filter((o) => catMatch(o, "needs_details")).length,
    abstractor: orders.filter((o) => catMatch(o, "abstractor")).length,
  };
  const selectKpi = (cat) => { setSlaOnly(false); setKpiCat((prev) => (prev === cat ? null : cat)); };
  const slaCount = orders.filter((o) => slaInfo(o).breached).length;
  const blockedCount = orders.filter((o) => (o.digital_search?.steps || []).some((s) => ["blocked", "unreachable", "failed"].includes(s.status))).length;

  let shown = slaOnly
    ? orders.filter((o) => slaInfo(o).breached)
    : kpiCat
      ? orders.filter((o) => catMatch(o, kpiCat))
      : orders.filter((o) => bucketOf(o) === tab);
  if (stateFilter) shown = shown.filter((o) => (o.state || "").toUpperCase() === stateFilter);
  if (productFilter) shown = shown.filter((o) => o.product_type === productFilter);
  if (statusFilter) shown = shown.filter((o) => o.status === statusFilter);
  // Closed Orders sub-filters (only when viewing the Closed tab, not a KPI/SLA drill-down)
  if (!kpiCat && !slaOnly && tab === "closed") {
    if (closedStatus) shown = shown.filter((o) => o.status === closedStatus);
    if (closedFrom) shown = shown.filter((o) => (o.updated_at || o.created_at || "") >= closedFrom);
    if (closedTo) shown = shown.filter((o) => (o.updated_at || o.created_at || "") <= closedTo + "T23:59:59");
  }
  if (blockedOnly) shown = shown.filter((o) => (o.digital_search?.steps || []).some((s) => ["blocked", "unreachable", "failed"].includes(s.status)));
  if (q.trim()) {
    const t = q.toLowerCase();
    shown = shown.filter((o) => [o.order_ref, o.base_ref, o.email_meta?.order_ref_raw, o.borrower, o.property_address, o.county, o.state, o.company].join(" ").toLowerCase().includes(t));
  }
  if (sortBy) {
    const val = (o) => {
      if (sortBy === "order_date") return o.order_date || o.created_at || "";
      if (sortBy === "assigned_to") return (o.assigned_to_name || o.assigned_to || "").toLowerCase();
      return String(o[sortBy] || "").toLowerCase();
    };
    shown = [...shown].sort((a, b) => {
      const x = val(a), y = val(b);
      if (x < y) return sortDir === "asc" ? -1 : 1;
      if (x > y) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
  }
  const toggleSort = (key) => {
    if (sortBy === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortBy(key); setSortDir("asc"); }
  };

  const exportSubmitted = async () => {
    try {
      const params = {};
      if (exportFrom) params.from_date = exportFrom;
      if (exportTo) params.to_date = exportTo;
      const res = await api.get("/orders/export/submitted", { params, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a"); a.href = url; a.download = "submitted_orders.csv"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Export downloaded");
    } catch (e) { toast.error("Export failed"); }
  };

  const TABS = [
    { key: "unaccepted", label: "Unaccepted Orders" },
    { key: "accepted", label: "Accepted Orders" },
    { key: "closed", label: "Closed Orders" },
  ];

  return (
    <div className="min-h-[calc(100vh-3.5rem)] bg-slate-100 -m-6 p-6 text-slate-800">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h1 className="font-display text-2xl font-700 tracking-tight text-[#1A395E]">Master Dashboard</h1>
          <p className="text-sm text-slate-500 mt-0.5">All times shown in Eastern Standard Time (EST). Track every live order and assign your team.</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
        <button data-testid="sync-now-btn" onClick={syncNow} disabled={syncing}
          className="inline-flex items-center gap-2 rounded-md border border-[#1A6FBF] bg-white px-3 py-2 text-sm font-semibold text-[#1A6FBF] hover:bg-blue-50 transition-colors shrink-0 disabled:opacity-60">
          <RefreshCw className={`h-4 w-4 ${syncing ? "animate-spin" : ""}`} /> {syncing ? "Syncing…" : "Sync now"}
        </button>
        <button data-testid="delete-all-orders-btn" onClick={deleteAllOrders}
          className="inline-flex items-center gap-2 rounded-md border border-red-300 bg-white px-3 py-2 text-sm font-semibold text-red-600 hover:bg-red-50 transition-colors shrink-0">
          <Trash2 className="h-4 w-4" /> Delete all orders
        </button>
        </div>
      </div>

      {/* New unclaimed order alert banner */}
      {newAlert.count > 0 && (
        <div data-testid="new-order-alert" className="mb-4 flex items-start gap-3 rounded-lg border border-[#1A6FBF] bg-blue-50 px-4 py-3 shadow-sm animate-in fade-in slide-in-from-top-2">
          <span className="relative mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#1A6FBF]">
            <Bell className="h-3.5 w-3.5 text-white" />
            <span className="absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full bg-red-500 ring-2 ring-blue-50" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-bold text-[#1A395E]" data-testid="new-order-alert-count">
              {newAlert.count} new unclaimed order{newAlert.count > 1 ? "s" : ""} just arrived
            </div>
            <div className="mt-0.5 truncate text-xs text-slate-600">
              {newAlert.orders.map((o) => `${o.order_ref}${o.state ? ` (${o.state})` : ""}`).join(" · ")}
              {newAlert.count > newAlert.orders.length ? " …" : ""}
            </div>
          </div>
          <button data-testid="new-order-alert-view" onClick={viewNewOrders}
            className="shrink-0 rounded-md bg-[#1A6FBF] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#155a9c] transition-colors">
            View unclaimed
          </button>
          <button data-testid="new-order-alert-dismiss" onClick={dismissAlert} aria-label="Dismiss"
            className="shrink-0 rounded-md p-1.5 text-slate-400 hover:bg-blue-100 hover:text-slate-600 transition-colors">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* KPI strip — clickable filters */}
      <OnTheClockWidget />
      {stats && (() => {
        const cards = [
          { key: "total", label: "Total Orders", value: kpiCounts.total, icon: Layers, accent: "text-[#1A395E]", ring: "ring-[#1A395E]" },
          { key: "unassigned", label: "Unassigned", value: kpiCounts.unassigned, icon: AlertCircle, accent: "text-amber-600", ring: "ring-amber-500" },
          { key: "inprogress", label: "In Progress", value: kpiCounts.inprogress, icon: Inbox, accent: "text-blue-700", ring: "ring-blue-600" },
          { key: "completed", label: "Completed", value: kpiCounts.completed, icon: CheckCircle2, accent: "text-emerald-600", ring: "ring-emerald-500" },
          { key: "submitted", label: "Submitted", value: kpiCounts.submitted, icon: Send, accent: "text-teal-600", ring: "ring-teal-500" },
          { key: "cancelled", label: "Cancelled", value: kpiCounts.cancelled, icon: XCircle, accent: "text-rose-600", ring: "ring-rose-500" },
          { key: "needs_details", label: "Needs Details", value: kpiCounts.needs_details, icon: AlertTriangle, accent: "text-amber-700", ring: "ring-amber-600" },
          { key: "abstractor", label: "Abstractor", value: kpiCounts.abstractor, icon: UserCheck, accent: "text-fuchsia-700", ring: "ring-fuchsia-600" },
        ];
        return (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-4">
            {cards.map((c) => {
              const Icon = c.icon;
              const active = kpiCat === c.key;
              return (
                <button key={c.key} data-testid={`kpi-${c.key}`} onClick={() => selectKpi(c.key)}
                  className={`text-left rounded-lg border bg-white p-3 flex items-center justify-between shadow-sm transition-all duration-150 hover:shadow-md hover:-translate-y-0.5 ${active ? `border-transparent ring-2 ${c.ring}` : "border-slate-200"}`}>
                  <div>
                    <div className="text-[11px] uppercase font-semibold text-slate-400">{c.label}</div>
                    <div className={`text-2xl font-700 ${c.accent}`}>{c.value}</div>
                  </div>
                  <Icon className={`h-5 w-5 ${c.accent}`} />
                </button>
              );
            })}
          </div>
        );
      })()}

      {kpiCat && (
        <div data-testid="kpi-filter-banner" className="mb-3 flex items-center gap-2 text-xs text-slate-600">
          <span className="rounded-full bg-[#1A395E] px-2.5 py-1 font-semibold text-white">
            Filtered by: {({ total: "All Orders", unassigned: "Unassigned", inprogress: "In Progress", completed: "Completed", submitted: "Submitted", cancelled: "Cancelled", needs_details: "Needs Details (blank Borrower/Property)", abstractor: "Abstractor (ProTitleUSA)" })[kpiCat]}
          </span>
          <button data-testid="kpi-filter-clear" onClick={() => setKpiCat(null)} className="font-semibold text-[#1A6FBF] hover:underline">Clear · back to tabs</button>
          {kpiCat === "needs_details" && kpiCounts.needs_details > 0 && (
            <button data-testid="reocr-all-blank-btn" onClick={reocrAllBlank} disabled={reOcr}
              className="ml-auto inline-flex items-center gap-1.5 rounded-md border border-amber-400 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-800 hover:bg-amber-100 transition-colors disabled:opacity-60">
              <RefreshCw className={`h-3.5 w-3.5 ${reOcr ? "animate-spin" : ""}`} /> {reOcr ? "Re-OCR running…" : `Re-OCR all blank (${kpiCounts.needs_details})`}
            </button>
          )}
          {kpiCat === "needs_details" && reprocessInfo && reprocessInfo.finished_at && reprocessInfo.status !== "idle" && (
            <span data-testid="reprocess-history" className={`${kpiCounts.needs_details > 0 ? "" : "ml-auto"} inline-flex items-center gap-1 text-[11px] text-slate-500`}>
              <History className="h-3 w-3" /> Last re-OCR: recovered <span className="font-semibold text-emerald-700">{reprocessInfo.updated || 0}</span>{reprocessInfo.still_blank ? <>, <span className="font-semibold text-amber-700">{reprocessInfo.still_blank} still blank</span></> : null} · {new Date(reprocessInfo.finished_at).toLocaleString()}
            </span>
          )}
        </div>
      )}

      {/* Search bar (Archer-style) */}
      <div className="rounded-lg border border-slate-200 bg-white p-3 mb-3 shadow-sm">
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-xs font-bold uppercase tracking-wide text-[#1A395E]">Search</span>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            <input data-testid="order-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Order #, borrower, property…"
              className="w-72 rounded-md border border-slate-300 bg-white pl-8 pr-3 py-1.5 text-sm text-slate-700 focus:ring-2 focus:ring-[#1A395E] focus:outline-none" />
          </div>
          <label className="text-xs font-semibold text-slate-500">State</label>
          <select data-testid="filter-state" value={stateFilter} onChange={(e) => setStateFilter(e.target.value)}
            className="rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-700 focus:ring-2 focus:ring-[#1A395E] focus:outline-none">
            <option value="">All States</option>
            {US_STATES.map((s) => <option key={s} value={s}>{stateName(s)}</option>)}
          </select>
          <label className="text-xs font-semibold text-slate-500">Product</label>
          <select data-testid="filter-product" value={productFilter} onChange={(e) => setProductFilter(e.target.value)}
            className="rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-700 focus:ring-2 focus:ring-[#1A395E] focus:outline-none">
            <option value="">All Products</option>
            {products.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <label className="text-xs font-semibold text-slate-500">Status</label>
          <select data-testid="filter-status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-700 focus:ring-2 focus:ring-[#1A395E] focus:outline-none">
            <option value="">All Statuses</option>
            {STATUS_STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          {(q || stateFilter || productFilter || statusFilter) && (
            <button data-testid="clear-filters" onClick={() => { setQ(""); setStateFilter(""); setProductFilter(""); setStatusFilter(""); }}
              className="text-xs text-[#1A395E] font-semibold hover:underline">Clear</button>
          )}
          <span className="ml-auto text-xs text-slate-500">{shown.length} shown</span>
          <div className="flex items-center gap-1.5 pl-2 border-l border-slate-200">
            <input data-testid="export-from" type="date" value={exportFrom} onChange={(e) => setExportFrom(e.target.value)}
              className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs text-slate-700 focus:outline-none" />
            <span className="text-xs text-slate-400">→</span>
            <input data-testid="export-to" type="date" value={exportTo} onChange={(e) => setExportTo(e.target.value)}
              className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs text-slate-700 focus:outline-none" />
            <button data-testid="export-submitted-csv" onClick={exportSubmitted}
              className="inline-flex items-center gap-1 rounded-md bg-teal px-2.5 py-1.5 text-xs font-semibold text-white hover:opacity-90 transition-opacity">
              <Download className="h-3.5 w-3.5" /> Export Submitted
            </button>
          </div>
        </div>
      </div>

      {/* Team Queues — jump into any employee's live queue (read-only admin view) */}
      {employees.length > 0 && (
        <div data-testid="team-queues" className="mb-3 flex flex-wrap items-center gap-2">
          <span className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Team Queues:</span>
          {employees.map((e) => (
            <button key={e.id} data-testid={`view-queue-${e.id}`} onClick={() => navigate(`/admin/employees/${e.id}/queue`)}
              className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-3 py-1 text-xs font-semibold text-[#1A395E] hover:bg-blue-50 transition-colors">
              <UserCircle2 className="h-3.5 w-3.5" /> {e.name}
            </button>
          ))}
          <button data-testid="auto-assign-rules-link" onClick={() => navigate("/admin/auto-assign")}
            className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-[#1A395E]/30 bg-[#1A395E]/5 px-3 py-1 text-xs font-semibold text-[#1A395E] hover:bg-[#1A395E]/10 transition-colors">
            <MapPin className="h-3.5 w-3.5" /> Auto-Assign Rules
          </button>
        </div>
      )}

      {/* Status tabs */}
      <div className="flex items-end gap-1 mb-0">
        {TABS.map((t) => (
          <button key={t.key} data-testid={`tab-${t.key}`} onClick={() => { setTab(t.key); setKpiCat(null); setSlaOnly(false); }}
            className={`rounded-t-md border border-b-0 px-4 py-2 text-sm font-semibold transition-colors ${
              (!kpiCat && !slaOnly && tab === t.key) ? "bg-[#1A395E] text-white border-[#1A395E]" : "bg-blue-50 text-[#1A395E] border-slate-200 hover:bg-blue-100"}`}>
            {t.label} — {countFor(t.key)}
          </button>
        ))}
        {slaCount > 0 && (
          <button data-testid="sla-filter-toggle" onClick={() => { setSlaOnly((v) => !v); setKpiCat(null); }}
            className={`ml-2 mb-1 inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-bold transition-colors ${
              slaOnly ? "border-rose-600 bg-rose-600 text-white" : "border-rose-300 bg-rose-50 text-rose-700 hover:bg-rose-100"}`}>
            <AlertTriangle className="h-3.5 w-3.5" /> SLA breached — {slaCount}
          </button>
        )}
        {blockedCount > 0 && (
          <button data-testid="blocked-portal-filter-toggle" onClick={() => { setBlockedOnly((v) => !v); setKpiCat(null); }}
            title="Duval orders where the auto-search hit a CAPTCHA/unreachable portal — a manual Tax lookup is likely needed"
            className={`ml-2 mb-1 inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-bold transition-colors ${
              blockedOnly ? "border-amber-600 bg-amber-600 text-white" : "border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100"}`}>
            <AlertCircle className="h-3.5 w-3.5" /> Needs manual lookup — {blockedCount}
          </button>
        )}
        {!kpiCat && !slaOnly && tab === "unaccepted" && countFor("unaccepted") > 0 && (
          <button data-testid="assign-all-suggested" onClick={assignAllSuggested}
            className="ml-auto mb-1 inline-flex items-center gap-1.5 rounded-md bg-[#1A6FBF] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#155a9c] transition-colors">
            ⚡ Assign all suggested
          </button>
        )}
        <button data-testid="merge-duplicates" onClick={mergeDuplicates} title="Merge duplicate order numbers (keeps the oldest row)"
          className={`${!kpiCat && !slaOnly && tab === "unaccepted" && countFor("unaccepted") > 0 ? "ml-2" : "ml-auto"} mb-1 inline-flex items-center gap-1.5 rounded-md border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-800 hover:bg-amber-100 transition-colors`}>
          <Layers className="h-3.5 w-3.5" /> Merge duplicates
        </button>
      </div>
      {!kpiCat && !slaOnly && tab === "closed" && (
        <div data-testid="closed-filters" className="mt-2 flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-white p-2.5 shadow-sm">
          <span className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Closed filters:</span>
          <div className="inline-flex overflow-hidden rounded-md border border-slate-300">
            {[["", "All"], ["Completed", "Completed"], ["Cancelled", "Cancelled"], ["Rejected", "Rejected"], ["Submitted", "Submitted"]].map(([val, lbl]) => (
              <button key={val || "all"} data-testid={`closed-status-${val || "all"}`} onClick={() => setClosedStatus(val)}
                className={`px-3 py-1.5 text-xs font-semibold transition-colors ${closedStatus === val ? "bg-[#1A395E] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
                {lbl}
              </button>
            ))}
          </div>
          <span className="ml-1 text-xs text-slate-400">Closed date</span>
          <input data-testid="closed-from" type="date" value={closedFrom} onChange={(e) => setClosedFrom(e.target.value)}
            className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs text-slate-700 focus:outline-none" />
          <span className="text-xs text-slate-400">→</span>
          <input data-testid="closed-to" type="date" value={closedTo} onChange={(e) => setClosedTo(e.target.value)}
            className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs text-slate-700 focus:outline-none" />
          <span className="ml-1 text-xs text-slate-500">{shown.length} shown</span>
          {(closedStatus || closedFrom || closedTo) && (
            <button data-testid="closed-filters-clear" onClick={() => { setClosedStatus(""); setClosedFrom(""); setClosedTo(""); }}
              className="text-xs font-semibold text-[#1A6FBF] hover:underline">Clear</button>
          )}
        </div>
      )}
      {slaOnly && (
        <div data-testid="sla-filter-banner" className="mt-2 flex items-center gap-2 text-xs text-slate-600">
          <span className="rounded-full bg-rose-600 px-2.5 py-1 font-semibold text-white">Showing {slaCount} order(s) past their stage SLA</span>
          <button onClick={() => setSlaOnly(false)} className="font-semibold text-[#1A6FBF] hover:underline">Clear</button>
        </div>
      )}
      {blockedOnly && (
        <div data-testid="blocked-filter-banner" className="mt-2 flex items-center gap-2 text-xs text-slate-600">
          <span className="rounded-full bg-amber-600 px-2.5 py-1 font-semibold text-white">Showing {blockedCount} order(s) with a blocked/unreachable portal — likely need a manual Tax lookup</span>
          <button onClick={() => setBlockedOnly(false)} className="font-semibold text-[#1A6FBF] hover:underline">Clear</button>
        </div>
      )}

      {/* Table (no horizontal scroll) */}
      <div className="rounded-b-lg rounded-tr-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
        <table className="w-full table-fixed text-left text-[13px]">
          <colgroup>
            <col className="w-[8%]" /><col className="w-[6%]" /><col className="w-[11%]" /><col className="w-[11%]" />
            <col className="w-[12%]" /><col className="w-[4%]" /><col className="w-[8%]" /><col className="w-[15%]" />
            <col className="w-[8%]" /><col className="w-[12%]" /><col className="w-[5%]" />
          </colgroup>
          <thead className="bg-[#1A395E] text-white">
            <tr className="text-[11px] uppercase tracking-wide">
              {[
                { k: "order_ref", label: "Order #" },
                { k: "customer_file_id", label: "Customer File" },
                { k: "order_date", label: "Order Date" },
                { k: "product_type", label: "Product" },
                { k: "borrower", label: "Borrower" },
                { k: "property_address", label: "Property" },
                { k: "state", label: "State" },
                { k: "county", label: "County" },
              ].map((c) => (
                <th key={c.k} data-testid={`sort-${c.k}`} onClick={() => toggleSort(c.k)}
                    className="px-2 py-2 font-semibold cursor-pointer select-none hover:bg-white/10">
                  <span className="inline-flex items-center gap-1">{c.label}
                    {sortBy === c.k ? (sortDir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-40" />}
                  </span>
                </th>
              ))}
              <th className="px-2 py-2 font-semibold">Summary</th>
              <th data-testid="sort-status" onClick={() => toggleSort("status")} className="px-2 py-2 font-semibold cursor-pointer select-none hover:bg-white/10">
                <span className="inline-flex items-center gap-1">Status
                  {sortBy === "status" ? (sortDir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-40" />}
                </span>
              </th>
              <th data-testid="sort-assigned_to" onClick={() => toggleSort("assigned_to")} className="px-2 py-2 font-semibold cursor-pointer select-none hover:bg-white/10">
                <span className="inline-flex items-center gap-1">Assigned To
                  {sortBy === "assigned_to" ? (sortDir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-40" />}
                </span>
              </th>
              <th data-testid="sort-submitted_at" onClick={() => toggleSort("submitted_at")} className="px-2 py-2 font-semibold cursor-pointer select-none hover:bg-white/10">
                <span className="inline-flex items-center gap-1">Delivered
                  {sortBy === "submitted_at" ? (sortDir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-40" />}
                </span>
              </th>
              <th className="px-2 py-2 font-semibold text-center">Open</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && <tr><td colSpan={12} className="px-3 py-10 text-center text-sm text-slate-400">Loading…</td></tr>}
            {!loading && shown.map((o, idx) => {
              const a = agingInfo(o);
              const sla = slaInfo(o);
              const isOpen = expanded === o.id;
              return (
                <React.Fragment key={o.id}>
                <tr data-testid={`admin-order-${o.id}`} onClick={() => navigate(`/orders/${o.id}`)} className={`cursor-pointer ${idx % 2 ? "bg-slate-50" : "bg-white"} hover:bg-blue-50/60 transition-colors ${sla.breached ? "ring-1 ring-inset ring-rose-200" : ""}`}>
                  <td className="px-2 py-2 align-top">
                    <button data-testid={`order-link-${o.id}`} onClick={() => navigate(`/orders/${o.id}`)}
                      className="font-mono text-[#1A6FBF] font-semibold hover:underline truncate block max-w-full text-left">{o.order_ref}</button>
                    {o.needs_review && <span className="inline-flex items-center gap-1 mt-0.5 rounded-full bg-red-100 px-1.5 py-0.5 text-[9px] font-bold text-red-700"><AlertCircle className="h-2.5 w-2.5" /> Review</span>}
                    {o.merged_count > 0 && <span data-testid={`merged-badge-${o.id}`} title={`Auto-merged: absorbed ${o.merged_count} duplicate email/order(s) for this file`} className="inline-flex items-center gap-1 mt-0.5 ml-1 rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-bold text-amber-700"><Layers className="h-2.5 w-2.5" /> Merged {o.merged_count}</span>}
                    {(!o.borrower || !o.property_address) && <span data-testid={`needs-details-badge-${o.id}`} title="Missing Borrower and/or Property — run Force Re-process on the Email Intake page to auto-fill from the original email" className="inline-flex items-center gap-1 mt-0.5 ml-1 rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-bold text-amber-800"><AlertTriangle className="h-2.5 w-2.5" /> Needs details</span>}
                    {o.digital_search?.status === "running" && <span data-testid={`portals-badge-${o.id}`} title="Auto digital search running on Duval county portals" className="inline-flex items-center gap-1 mt-0.5 ml-1 rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-bold text-amber-700"><Loader2 className="h-2.5 w-2.5 animate-spin" /> Searching…</span>}
                    {o.digital_search?.status === "done" && o.digital_search?.captured > 0 && <span data-testid={`portals-badge-${o.id}`} title={`Auto-searched Duval portals — ${o.digital_search.captured} capture(s) attached`} className="inline-flex items-center gap-1 mt-0.5 ml-1 rounded-full bg-emerald-100 px-1.5 py-0.5 text-[9px] font-bold text-emerald-700"><CheckCircle2 className="h-2.5 w-2.5" /> Portals ✓ {o.digital_search.captured}</span>}
                    {(() => {
                      const asla = abstractorSla(o);
                      if (!asla) return null;
                      const cls = { red: "bg-rose-100 text-rose-700", amber: "bg-amber-100 text-amber-800", green: "bg-emerald-100 text-emerald-700", slate: "bg-slate-100 text-slate-600" }[asla.level];
                      return <span data-testid={`abstractor-sla-${o.id}`} title={`ProTitleUSA · ${asla.label}`} className={`inline-flex items-center gap-1 mt-0.5 ml-1 rounded-full px-1.5 py-0.5 text-[9px] font-bold ${cls}`}><Clock className="h-2.5 w-2.5" /> {asla.label}</span>;
                    })()}
                  </td>
                  <td data-testid={`cust-file-${o.id}`} className="px-2 py-2 align-top font-mono text-[12px] text-slate-600 truncate max-w-[120px]" title={o.customer_file_id || ""}>{o.customer_file_id || "—"}</td>
                  <td className="px-2 py-2 align-top text-slate-500 whitespace-nowrap" title={(o.order_date || o.created_at) ? new Date(o.order_date || o.created_at).toLocaleString() : ""}>
                    {(o.order_date || o.created_at) ? new Date(o.order_date || o.created_at).toLocaleDateString([], { month: "short", day: "numeric" }) : "—"}
                    {o.submitted_at && (
                      <div data-testid={`delivered-${o.id}`} className="mt-0.5 inline-flex items-center gap-1 text-[10px] font-semibold text-teal-700" title={`Delivered ${new Date(o.submitted_at).toLocaleString()}`}>
                        <Send className="h-2.5 w-2.5" /> {new Date(o.submitted_at).toLocaleDateString([], { month: "short", day: "numeric" })}
                      </div>
                    )}
                  </td>
                  <td className="px-2 py-2 align-top text-slate-700 truncate" title={o.product_type}>{o.product_type}</td>
                  <td className="px-2 py-2 align-top text-slate-700 truncate" title={o.borrower}>{o.borrower || "—"}</td>
                  <td className="px-2 py-2 align-top text-slate-600 truncate" title={o.property_address}>{cityOf(o.property_address) || o.property_address || "—"}</td>
                  <td className="px-2 py-2 align-top" onClick={(e) => e.stopPropagation()}>
                    <select data-testid={`state-select-${o.id}`} value={o.state || ""} onChange={(e) => setOrderState(o.id, e.target.value)}
                      className={`w-full rounded border px-1 py-0.5 text-[12px] font-semibold focus:outline-none ${o.state ? "border-slate-300 text-slate-700 bg-white" : "border-amber-400 text-amber-700 bg-amber-50"}`}>
                      <option value="" disabled={!!o.state}>— set —</option>
                      {US_STATES.map((s) => <option key={s} value={s}>{stateName(s)}</option>)}
                    </select>
                  </td>
                  <td className="px-2 py-2 align-top text-slate-600 truncate" title={o.county}>{o.county || "—"}</td>
                  <td data-testid={`order-summary-${o.id}`} className="px-2 py-2 align-top text-slate-500 text-[12px]" title={o.ai_summary?.content || ""}>
                    <span className="line-clamp-2">{o.ai_summary?.content || "—"}</span>
                  </td>
                  <td className="px-2 py-2 align-top">
                    <select data-testid={`row-status-${o.id}`} value={o.status || "Open Order"}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => { e.stopPropagation(); updateStatus(o, e.target.value); }}
                      className="w-full max-w-[150px] rounded-md border border-slate-300 bg-white px-1.5 py-1 text-xs font-semibold text-slate-700 focus:ring-2 focus:ring-[#1A395E] focus:outline-none cursor-pointer">
                      {STATUS_STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
                      {o.status && !STATUS_STAGES.includes(o.status) && <option value={o.status}>{o.status}</option>}
                    </select>
                    {sla.breached && <div data-testid={`sla-pill-${o.id}`} className="mt-1 inline-flex items-center gap-1 rounded-full bg-rose-100 px-1.5 py-0.5 text-[9px] font-bold text-rose-700" title={`In "${o.status}" for ${Math.floor(sla.hours)}h · limit ${sla.threshold}h`}><AlertTriangle className="h-2.5 w-2.5" /> SLA {Math.floor(sla.hours)}h/{sla.threshold}h</div>}
                    {a && !sla.breached && <div className={`mt-1 inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[9px] font-bold ${a.level === "red" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}><Clock className="h-2.5 w-2.5" />{a.label}</div>}
                    <div className="mt-1"><RequirementsProgress order={o} testid={`req-progress-${o.id}`} /></div>
                    <button data-testid={`stage-history-toggle-${o.id}`} onClick={(e) => { e.stopPropagation(); setExpanded(isOpen ? null : o.id); }}
                      className="mt-1 inline-flex items-center gap-1 text-[10px] font-semibold text-slate-500 hover:text-[#1A6FBF] transition-colors">
                      <History className="h-3 w-3" /> Stage history
                      <ChevronDown className={`h-3 w-3 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                    </button>
                  </td>
                  <td className="px-2 py-2 align-top" onClick={(e) => e.stopPropagation()}>
                    <select data-testid={`assign-select-${o.id}`} value={o.assigned_to || ""} onChange={(e) => assign(o.id, e.target.value)}
                      className="w-full rounded-md border border-slate-300 bg-white px-1.5 py-1 text-[12px] text-slate-700 focus:ring-2 focus:ring-[#1A395E] focus:outline-none">
                      <option value="">— Unassigned —</option>
                      {employees.map((e) => <option key={e.id} value={e.id}>{e.name}{e.role === "admin" ? " · Admin" : e.job_role ? ` · ${e.job_role.split(" ")[0]}` : ""}</option>)}
                    </select>
                    {!o.assigned_to && (() => {
                      const sug = suggestFor();
                      return sug ? (
                        <button data-testid={`suggest-${o.id}`} onClick={() => assign(o.id, sug.id)}
                          className="mt-1 inline-flex items-center gap-1 rounded border border-blue-200 bg-blue-50 px-1.5 py-0.5 text-[10px] font-semibold text-[#1A6FBF] hover:bg-blue-100 transition-colors">
                          ⚡ Suggest: {sug.name.split(" ")[0]}
                        </button>
                      ) : null;
                    })()}
                  </td>
                  <td data-testid={`delivered-cell-${o.id}`} className="px-2 py-2 align-top text-teal-700 whitespace-nowrap text-[12px]" title={o.submitted_at ? new Date(o.submitted_at).toLocaleString() : ""}>
                    {o.submitted_at ? new Date(o.submitted_at).toLocaleDateString([], { month: "short", day: "numeric", year: "2-digit" }) : "—"}
                  </td>
                  <td className="px-2 py-2 align-top text-center">
                    <button data-testid={`admin-open-${o.id}`} onClick={() => navigate(`/orders/${o.id}`)}
                      className="rounded-md bg-[#1A395E] px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-[#24497A] transition-colors">Open</button>
                  </td>
                </tr>
                {isOpen && (
                  <tr data-testid={`stage-history-row-${o.id}`} className="bg-slate-50/80">
                    <td colSpan={11} className="px-4 py-3">
                      <div className="text-[11px] font-bold uppercase tracking-wide text-slate-400 mb-2">Stage timeline — {o.order_ref}</div>
                      <div className="flex flex-wrap items-stretch gap-2">
                        {historyWithDurations(o).length === 0 && <div className="text-xs text-slate-400">No history yet.</div>}
                        {historyWithDurations(o).map((ev, i) => (
                          <div key={i} className={`rounded-md border px-2.5 py-1.5 text-[11px] ${ev.isCurrent ? "border-[#00BFA6] bg-[#00BFA6]/10" : "border-slate-200 bg-white"}`}>
                            <div className="font-semibold text-slate-700">{ev.status}{ev.isCurrent && <span className="ml-1 text-[9px] font-bold text-[#0f766e]">· current</span>}</div>
                            <div className="text-slate-500">{new Date(ev.timestamp).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</div>
                            <div className="mt-0.5 inline-flex items-center gap-1 font-semibold text-slate-600"><Clock className="h-3 w-3 text-slate-400" />{ev.isCurrent ? "in stage " : "held "}{humanizeDuration(ev.durationMs)}</div>
                            {ev.changed_by_name && <div className="text-slate-400">by {ev.changed_by_name}</div>}
                          </div>
                        ))}
                      </div>
                    </td>
                  </tr>
                )}
                </React.Fragment>
              );
            })}
            {!loading && !shown.length && <tr><td colSpan={11} className="px-3 py-12 text-center text-sm text-slate-400">No orders in this tab.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
